import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { errMessage } from "../../lib/errors";
import { Upload, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

function resolveSrc(url) {
  if (!url) return null;
  return url.startsWith("/") ? `${BACKEND}${url}` : url;
}

/** Image URL input + direct photo upload button + thumbnail preview.
 *  variant="table" (compact row cell) | "form" (new-dish form). */
export default function DishImageField({ value, onChange, testId, toast, variant = "table" }) {
  const [url, setUrl] = useState(value || "");
  const [progress, setProgress] = useState(null); // null = idle, 0-100 = uploading
  const fileRef = useRef(null);

  useEffect(() => { setUrl(value || ""); }, [value]);

  const pick = () => fileRef.current?.click();

  const upload = async (file) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast?.({ title: "Image too large", description: "Please keep photos under 8 MB." });
      if (fileRef.current) fileRef.current.value = "";
      return;
    }
    setProgress(0);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/admin/media/upload`, fd, {
        onUploadProgress: (e) => {
          if (e.total) setProgress(Math.round((e.loaded / e.total) * 100));
        },
      });
      setUrl(data.url);
      onChange(data.url);
      toast?.({ title: "Photo uploaded", description: "Now live on the menu." });
    } catch (e) {
      toast?.({ title: "Upload failed", description: errMessage(e) });
    } finally {
      setProgress(null);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const compact = variant === "table";
  return (
    <div className={`flex items-center gap-2 ${compact ? "" : "w-full"}`}>
      {url ? (
        <img
          src={resolveSrc(url)}
          alt="dish"
          className={`object-cover border border-[var(--mm-line)] shrink-0 ${compact ? "w-9 h-9" : "w-12 h-12"}`}
          data-testid={testId ? `${testId}-thumb` : undefined}
        />
      ) : (
        <div className={`shrink-0 border border-dashed border-[var(--mm-line)] ${compact ? "w-9 h-9" : "w-12 h-12"}`} />
      )}
      <input
        value={url}
        onChange={(e) => setUrl(e.target.value)}
        onBlur={(e) => { if (e.target.value !== (value || "")) onChange(e.target.value); }}
        placeholder="Paste image URL"
        className={`bg-transparent border border-[var(--mm-line)] text-[var(--mm-cream)] px-2 py-1 text-xs focus:border-[var(--mm-accent)] outline-none ${compact ? "w-36" : "flex-1 py-2 text-sm"}`}
        data-testid={testId}
      />
      <button
        type="button"
        onClick={pick}
        disabled={progress !== null}
        title="Upload a photo from your device"
        data-testid={testId ? `${testId}-upload` : undefined}
        className="shrink-0 inline-flex items-center gap-1.5 px-2 py-1.5 border border-[var(--mm-line)] text-[var(--mm-cream-muted)] hover:border-[var(--mm-accent)] hover:text-[var(--mm-accent)] transition-colors text-[0.62rem] tracking-[0.14em] uppercase disabled:opacity-50"
      >
        {progress !== null ? (
          <>
            <Loader2 size={12} className="animate-spin" /> {progress}%
          </>
        ) : (
          <>
            <Upload size={12} /> Upload
          </>
        )}
      </button>
      <input
        ref={fileRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        className="hidden"
        onChange={(e) => upload(e.target.files?.[0])}
      />
    </div>
  );
}
